import React, { useRef, useEffect, memo } from 'react';
import { Box, Typography, List, ListItem, ListItemText, Divider, styled } from '@mui/material';
import { useTranslation } from 'next-i18next';

const DropdownContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  left: 0,
  top: '100%',
  maxHeight: '450px',
  overflowY: 'auto',
  backgroundColor: 'white',
  borderRadius: '10px',
  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
  zIndex: 1000,
  marginTop: '5px',
  padding: '32px',
  opacity: 0,
  transform: 'translateY(-10px)',
  transition: 'opacity 0.2s ease, transform 0.2s ease',
  '&.visible': {
    opacity: 1,
    transform: 'translateY(0)'
  },
  '&::-webkit-scrollbar': {
    width: '1px',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent !important',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#575758',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#F4762A',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: '#575758 transparent',
}));

const CategoryHeader = styled(Box)(({ theme }) => ({
  top: 0,
  zIndex: 1,
  padding: '12px 16px',
  fontWeight: 500,
  fontSize: '16px',
  color: '#333',
  backgroundColor: 'white',
}));

interface LocationDropdownProps {
  isVisible: boolean;
  locationCategories: string[];
  onLocationClick: (locationType: string) => void;
  anchorEl: HTMLElement | null;
  onClose?: () => void;
}

const LocationDropdown: React.FC<LocationDropdownProps> = memo(({
  isVisible,
  locationCategories,
  onLocationClick,
  anchorEl,
  onClose
}) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (isVisible && dropdownRef.current) {
      setTimeout(() => {
        if (dropdownRef.current) {
          dropdownRef.current.classList.add('visible');
        }
      }, 10);
    } else if (dropdownRef.current) {
      dropdownRef.current.classList.remove('visible');
    }
  }, [isVisible]);

  useEffect(() => {
    if (!anchorEl || !dropdownRef.current) return;

    const searchContainer = anchorEl.closest('div[class*="MuiInputBase-root"]') || anchorEl.parentElement;
    
    if (searchContainer) {
      const rect = searchContainer.getBoundingClientRect();
      const dropdown = dropdownRef.current;
      
      dropdown.style.width = `${rect.width}px`;
      
      dropdown.style.left = '0px';
      dropdown.style.top = '100%';
    }
  }, [anchorEl]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && 
          !dropdownRef.current.contains(event.target as Node) && 
          anchorEl && 
          !anchorEl.contains(event.target as Node) &&
          onClose) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, anchorEl, onClose]);

  if (!isVisible) return null;

  return (
    <DropdownContainer ref={dropdownRef}>
      <CategoryHeader>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          {t('search_by')}
        </Typography>
      </CategoryHeader>
      <List dense sx={{ p: 0 }}>
        {locationCategories.map((category, index) => (
          <React.Fragment key={index}>
            <ListItem 
              button 
              onClick={() => onLocationClick(t(category))}
              sx={{ 
                py: 1.5,
                '&:hover': { 
                  backgroundColor: '#F4762A10',
                  color: '#F4762A',
                },
                cursor: 'pointer'
              }}
            >
              <ListItemText 
                primary={t(category)} 
                primaryTypographyProps={{
                  style: { fontFamily: 'Prompt, sans-serif', fontSize: '16px' }
                }}
              />
            </ListItem>
            {index < locationCategories.length - 1 && <Divider component="li" />}
          </React.Fragment>
        ))}
      </List>
    </DropdownContainer>
  );
});

export default LocationDropdown; 